import { Suspense, lazy, useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Navbar from '../component/Navbar/Main'
import Footer from '../component/Footer/Main'
import ProtectedRoute from '../component/Manage/ProtectedRoute'
import RoleGuard from '../component/Manage/RoleGuard'

// Public pages
const Home = lazy(() => import('../component/Home/Main'));
const About = lazy(() => import('../component/About/Main'));
const OurRoom = lazy(() => import('../component/Rooms/OurRoom/Main'));
const RoomDetails = lazy(() => import('../component/Rooms/RoomDetails/Main'));
const Service = lazy(() => import('../component/Facilities/Service/Main'));
const ServiceDetails = lazy(() => import('../component/Facilities/ServiceDetails/Main'));
const Faq = lazy(() => import('../component/Faq/Main'));
const Contact = lazy(() => import('../component/Contact/Main'));

// Manage pages
const ManageLogin = lazy(() => import('../component/Manage/Login'));
const ManageLayout = lazy(() => import('../component/Manage/Layout'));
const ManageDashboard = lazy(() => import('../component/Manage/Dashboard'));
const ManageUsers = lazy(() => import('../component/Manage/pages/Users'));
const ManageItems = lazy(() => import('../component/Manage/pages/SupplyItems'));
const ManageCosts = lazy(() => import('../component/Manage/pages/Costs'));
const ManageSupplyTransactions = lazy(() => import('../component/Manage/pages/SupplyTransactions'));
const ManageRooms = lazy(() => import('../component/Manage/pages/Rooms'));
const ManageRoomCleaning = lazy(() => import('../component/Manage/pages/RoomCleaning'));
const ManageMaintenance = lazy(() => import('../component/Manage/Maintenance'));
const ManageCostTransactions = lazy(() => import('../component/Manage/pages/CostTransactions'));
const ManageReport = lazy(() => import('../component/Manage/pages/Report'));
const ManageReportStock = lazy(() => import('../component/Manage/pages/reports/StockReport'));
const ManageReportSupplyIn = lazy(() => import('../component/Manage/pages/reports/SupplyInReport'));
const ManageReportSupplyOut = lazy(() => import('../component/Manage/pages/reports/SupplyOutReport'));
const ManageReportSupplyOutItem = lazy(() => import('../component/Manage/pages/reports/SupplyOutByItemReport'));
const ManageReportLowStock = lazy(() => import('../component/Manage/pages/reports/LowStockReport'));
const ManageReportCostSummary = lazy(() => import('../component/Manage/pages/reports/CostSummaryReport'));
const ManageReportCleaning = lazy(() => import('../component/Manage/pages/reports/CleaningHistoryReport'));
const ManageReportRoomStatus = lazy(() => import('../component/Manage/pages/reports/RoomStatusReport'));
const ManageReportStockCard = lazy(() => import('../component/Manage/pages/reports/StockCardReport'));
const ManageReportSupplyOutStaff = lazy(() => import('../component/Manage/pages/reports/SupplyOutByStaffReport'));

const Routing = () => {
    const [showNav, setShowNav] = useState(true);
    const [showFooter, setShowFooter] = useState(true);
    const location = useLocation();

    const isManagePage = location.pathname.startsWith('/manage');

    useEffect(() => {
        if (isManagePage) {
            setShowNav(false);
            setShowFooter(false);
            return;
        }
        const isHome3 = location.pathname === "/home-03";
        const isHome4 = location.pathname === "/home-04";
        setShowNav(!isHome3 && !isHome4);
        setShowFooter(!isHome3);
        window.scrollTo(0, 0);
    }, [location.pathname, isManagePage]);

    return (
        <>
            {showNav && <Navbar />}
            <Suspense fallback={<div className="text-center py-5">Loading...</div>}>
                <Routes>
                    {/* ── Public routes ── */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/room" element={<OurRoom />} />
                    <Route path="/room-details" element={<RoomDetails />} />
                    <Route path="/amenities" element={<Service />} />
                    <Route path="/faq" element={<Faq />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/service-details" element={<ServiceDetails />} />

                    {/* ── Manage routes ── */}
                    <Route path="/manage/login" element={<ManageLogin />} />
                    <Route path="/manage/maintenance" element={<ManageMaintenance />} />
                    <Route
                        path="/manage"
                        element={
                            <ProtectedRoute>
                                <ManageLayout />
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="/manage/dashboard" replace />} />
                        <Route path="dashboard" element={<ManageDashboard />} />
                        <Route path="users" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor', 'housekeeper_leader']}>
                                <ManageUsers />
                            </RoleGuard>
                        } />
                        <Route path="rooms" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}>
                                <ManageRooms />
                            </RoleGuard>
                        } />
                        <Route path="supply/items" element={<ManageItems />} />
                        <Route path="supply/costs" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}>
                                <ManageCosts />
                            </RoleGuard>
                        } />
                        <Route path="supply/transactions" element={<ManageSupplyTransactions />} />
                        <Route path="room-cleaning" element={<ManageRoomCleaning />} />
                        <Route path="cost-transactions" element={
                            <RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}>
                                <ManageCostTransactions />
                            </RoleGuard>
                        } />
                        <Route path="report" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReport /></RoleGuard>} />
                        <Route path="report/stock" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportStock /></RoleGuard>} />
                        <Route path="report/supply-in" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportSupplyIn /></RoleGuard>} />
                        <Route path="report/supply-out" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportSupplyOut /></RoleGuard>} />
                        <Route path="report/supply-out-item" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportSupplyOutItem /></RoleGuard>} />
                        <Route path="report/low-stock" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportLowStock /></RoleGuard>} />
                        <Route path="report/cost-summary" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportCostSummary /></RoleGuard>} />
                        <Route path="report/cleaning-history" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportCleaning /></RoleGuard>} />
                        <Route path="report/room-status" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportRoomStatus /></RoleGuard>} />
                        <Route path="report/stock-card" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportStockCard /></RoleGuard>} />
                        <Route path="report/supply-out-staff" element={<RoleGuard allowedRoles={['system_operation', 'owner', 'supervisor']}><ManageReportSupplyOutStaff /></RoleGuard>} />
                    </Route>
                </Routes>
            </Suspense>
            {showFooter && <Footer />}
        </>
    );
};

export default Routing;
